import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "aurora-ribbons";

const DEFAULT_BG = "#050816";
const DEFAULT_RIBBON_A = "#7c3aed";
const DEFAULT_RIBBON_B = "#06b6d4";

// Each ribbon is described by a base Y (as a fraction of canvas height), an
// amplitude, a vertical thickness, two phase offsets used to drive its top
// and bottom edges, and which user color slot it borrows from.
//
// The shapes are built each frame from sums of low-frequency sines (slow,
// organic motion) and rendered with a heavy canvas blur for the glow.
const RIBBONS = [
  { yFrac: 0.32, amp: 0.10, thickness: 0.18, phaseTop: 0.0,  phaseBottom: 1.7, colorIdx: 1, alpha: 0.55 },
  { yFrac: 0.50, amp: 0.13, thickness: 0.22, phaseTop: 2.3,  phaseBottom: 4.1, colorIdx: 2, alpha: 0.50 },
  { yFrac: 0.68, amp: 0.09, thickness: 0.16, phaseTop: 4.9,  phaseBottom: 6.4, colorIdx: 1, alpha: 0.45 },
];

const STAR_COUNT = 60;          // tiny twinkling stars behind the ribbons
const STEP_X = 14;              // sample step when building a ribbon outline (px)
const TIME_STEP = 0.0035;       // animation speed
const BLUR_PX = 38;             // canvas filter blur for the ribbon glow

export default function AuroraRibbons({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_RIBBON_A, DEFAULT_RIBBON_B]);
  const starsRef = useRef([]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_RIBBON_A),
      pickColor(colors, 2, DEFAULT_RIBBON_B),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;

    const seedStars = () => {
      const arr = [];
      for (let i = 0; i < STAR_COUNT; i += 1) {
        arr.push({
          x: Math.random() * width,
          y: Math.random() * height * 0.85,
          base: 0.3 + Math.random() * 0.5,
          twinklePhase: Math.random() * Math.PI * 2,
          twinkleSpeed: 0.8 + Math.random() * 1.6,
          radius: 0.6 + Math.random() * 0.9,
        });
      }
      starsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedStars();
    };

    const drawBackground = (bg) => {
      // Vertical gradient: pure bg at top, slightly lifted at bottom for depth.
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, bg);
      grad.addColorStop(1, rgba(bg, 0.85));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);
    };

    const drawStars = () => {
      const stars = starsRef.current;
      ctx.fillStyle = "rgba(255,255,255,1)";
      for (let i = 0; i < stars.length; i += 1) {
        const s = stars[i];
        const tw = 0.5 + 0.5 * Math.sin(t * s.twinkleSpeed + s.twinklePhase);
        const a = s.base * tw;
        ctx.globalAlpha = a;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const drawRibbon = (ribbon, color) => {
      const baseY = ribbon.yFrac * height;
      const amp = ribbon.amp * height;
      const thickness = ribbon.thickness * height;

      ctx.fillStyle = rgba(color, ribbon.alpha);
      ctx.beginPath();
      // Top edge (left → right)
      for (let x = -STEP_X; x <= width + STEP_X; x += STEP_X) {
        const u = x / width;
        const y =
          baseY +
          Math.sin(u * 4.0 + t * 1.1 + ribbon.phaseTop) * amp +
          Math.sin(u * 9.0 + t * 0.6 + ribbon.phaseTop * 0.5) * (amp * 0.35);
        if (x === -STEP_X) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      // Bottom edge (right → left)
      for (let x = width + STEP_X; x >= -STEP_X; x -= STEP_X) {
        const u = x / width;
        const y =
          baseY +
          thickness +
          Math.sin(u * 3.4 + t * 0.9 + ribbon.phaseBottom) * amp +
          Math.sin(u * 7.5 + t * 0.5 + ribbon.phaseBottom * 0.3) * (amp * 0.35);
        ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
    };

    const tick = () => {
      const [bg, c1, c2] = colorsRef.current;
      const ribbonColors = [bg, c1, c2];

      drawBackground(bg);
      drawStars();

      // Render ribbons through a blur filter for the soft glow. Use
      // "lighter" composite so overlapping ribbons brighten instead of
      // muddying each other.
      const supportsFilter = "filter" in ctx;
      if (supportsFilter) ctx.filter = `blur(${BLUR_PX}px)`;
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < RIBBONS.length; i += 1) {
        const r = RIBBONS[i];
        drawRibbon(r, ribbonColors[r.colorIdx]);
      }
      // Crisper highlight pass on top, narrower & brighter, no blur.
      if (supportsFilter) ctx.filter = "blur(6px)";
      for (let i = 0; i < RIBBONS.length; i += 1) {
        const r = RIBBONS[i];
        drawRibbon(
          { ...r, thickness: r.thickness * 0.45, alpha: r.alpha * 0.6 },
          ribbonColors[r.colorIdx],
        );
      }
      ctx.globalCompositeOperation = "source-over";
      if (supportsFilter) ctx.filter = "none";

      t += TIME_STEP;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
